import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Buddies, BUDDIES_BUSY, BUDDY_HOME_ATTRIBUTE, type BuddiesWho } from '@aibuild/ui';
import InkGradient from './sketch/InkGradient';
import type { Box } from './sketch/sketchLayout';
import { pill, roundedRect } from './sketch/sketchPaths';
import { useInk } from './sketch/useInk';
import { Ink, Label } from './sketch/BuildSketch.styled';
import { BUDDY_HOME_THUMBNAIL, FILLING_BUDDIES_PX, FILLING_REDRAW_MS } from './builderWorkspace.constants';
import { FillingRoot, Home, SketchArea, SketchFrame } from './DetailsFilling.styled';

interface DetailsFillingProps {
  /** A buddy of its own, while it draws the thumbnail and the other builds the page. */
  readonly buddy?: BuddiesWho;
}

const LABELS = defineMessages({
  prompt: { id: 'apps.builder.details.prompt', defaultMessage: 'Instructions for the agent' },
  thumbnail: { id: 'apps.builder.details.thumbnail', defaultMessage: 'Thumbnail' },
  name: { id: 'apps.builder.details.name', defaultMessage: 'Name' },
  tagline: { id: 'apps.builder.details.tagline', defaultMessage: 'Tagline' },
});

const WIDTH = 280;
const PAD = 16;
const FIELDS = [
  { label: LABELS.prompt, height: 96, shape: roundedRect },
  { label: LABELS.thumbnail, height: 140, shape: roundedRect },
  { label: LABELS.name, height: 36, shape: pill },
  { label: LABELS.tagline, height: 36, shape: pill },
] as const;

const FIELD_BOXES = FIELDS.reduce<{ readonly boxes: Box[]; readonly top: number }>(
  ({ boxes, top }, field) => ({
    boxes: [...boxes, { x: PAD, y: top + 24, w: WIDTH - PAD * 2, h: field.height }],
    top: top + 24 + field.height + 28,
  }),
  { boxes: [], top: 8 },
);

const THUMBNAIL_BOX = FIELD_BOXES.boxes[1];

const DetailsFilling: React.FC<DetailsFillingProps> = ({ buddy }) => {
  const intl = useIntl();
  const { id, ink } = useInk();
  const [round, setRound] = useState(0);
  const height = FIELD_BOXES.top;

  useEffect(() => {
    const timer = window.setInterval(() => setRound((value) => value + 1), FILLING_REDRAW_MS);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <FillingRoot aria-busy="true" data-testid="app-builder__details-filling">
      <SketchFrame>
        <SketchArea viewBox={`0 0 ${WIDTH} ${height}`} aria-hidden="true">
          <InkGradient id={id} width={WIDTH} height={height} />
          <g key={round}>
            {FIELDS.map((field, index) => {
              const box = FIELD_BOXES.boxes[index];
              return (
                <g key={field.label.id}>
                  <Label x={box.x} y={box.y - 10}>
                    {intl.formatMessage(field.label)}
                  </Label>
                  <Ink d={field.shape(box)} stroke={ink} pathLength={1} />
                </g>
              );
            })}
          </g>
        </SketchArea>
        <Home
          $box={THUMBNAIL_BOX}
          $width={WIDTH}
          $height={height}
          {...{ [BUDDY_HOME_ATTRIBUTE]: BUDDY_HOME_THUMBNAIL }}
        >
          {buddy && <Buddies who={buddy} act={BUDDIES_BUSY} size={FILLING_BUDDIES_PX} />}
        </Home>
      </SketchFrame>
    </FillingRoot>
  );
};

export default DetailsFilling;
